import { Component, OnInit, OnDestroy, signal } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatDialog } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { NgxPermissionsModule, NgxPermissionsService } from 'ngx-permissions';
import { NgxDatatableModule, ColumnMode } from '@swimlane/ngx-datatable';
import { VoltarIndicatorComponent } from '../../../../shared/components/voltar-indicator/voltar-indicator.component';
import { ConsultantsService } from '../../../../core/services/consultants.service';
import {
  ConsultantDetail,
  ConsultantTodayPanel,
  ConsultantScheduledDay,
  ConsultantCompletedDay,
  ConsultantPendingDay,
} from '../../../../shared/models/consultant';
import { AlertType } from '../../../../shared/models/alertType';
import { showAlertDialog } from '../../../../shared/helpers/showAlertDialog';
import { finalize, Subscription } from 'rxjs';
import { CapitalizeWordsPipe } from '../../../../shared/pipes/capitalize-words-pipe';
import { BadgeComponent, BadgeVariant } from '../../../../shared/components/badge/badge.component';
import { SearchFilterBarComponent, FilterConfig, FilterValues } from '../../../../shared/components/search-filter-bar/search-filter-bar.component';
import { GoogleMapComponent } from '../../../roteirizacao/components/map/google-map.component';
import { MapMarker } from '../../../roteirizacao/components/map/map.models';
import { ConsultantLocationTrackingService } from '../../../../core/services/consultant-location-tracking.service';

@Component({
  selector: 'app-consultores-detalhe',
  imports: [
    CommonModule,
    DatePipe,
    MatIconModule,
    MatProgressSpinnerModule,
    NgxPermissionsModule,
    NgxDatatableModule,
    VoltarIndicatorComponent,
    CapitalizeWordsPipe,
    BadgeComponent,
    SearchFilterBarComponent,
    GoogleMapComponent,
  ],
  templateUrl: './consultores-detalhe.component.html',
  styleUrl: './consultores-detalhe.component.scss',
})
export class ConsultoresDetalheComponent implements OnInit, OnDestroy {
  ColumnMode = ColumnMode;
  consultantId!: number;
  consultant?: ConsultantDetail;
  isLoading = true;
  isResettingPassword = false;
  canResetPassword = false;

  mapMarkers = signal<MapMarker[]>([]);
  private locationSubscription?: Subscription;

  // Em Tempo Real
  consultantToday: ConsultantTodayPanel | null = null;
  todayDateFormatted: string = this.formatTodayDate();

  // Pendências
  pendingItems: ConsultantPendingDay[] = [];

  // Rotas Programadas
  scheduledDays: ConsultantScheduledDay[] = [];
  scheduledDaysPage = 0;
  scheduledDaysPageSize = 5;
  totalScheduledDays = 0;
  scheduledDaysSearch = '';
  isScheduledDaysLoading = false;

  // Rotas Realizadas
  completedDays: ConsultantCompletedDay[] = [];
  completedDaysPage = 0;
  completedDaysPageSize = 5;
  totalCompletedDays = 0;
  completedDaysSearch = '';
  isCompletedDaysLoading = false;

  rotasRealizadasFilters: FilterConfig[] = [];
  rotasProgramadasFilters: FilterConfig[] = [];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private consultantsService: ConsultantsService,
    private dialog: MatDialog,
    private permissionsService: NgxPermissionsService,
    private locationTracking: ConsultantLocationTrackingService
  ) {}

  ngOnInit(): void {
    this.consultantId = Number(this.route.snapshot.paramMap.get('id'));
    this.loadConsultant();
    this.loadConsultantToday();
    this.loadPendingItems();
    this.loadScheduledDays();
    this.loadCompletedDays();

    this.locationTracking.startTracking(this.consultantId);
    this.locationSubscription = this.locationTracking.location$.subscribe((update) => {
      this.mapMarkers.set([
        { latitude: update.latitude, longitude: update.longitude, label: this.consultant?.nome ?? 'Consultor' }
      ]);
    });

    this.permissionsService
      .hasPermission('Permissions.Consultants.ResetPassword')
      .then((hasPermission) => {
        this.canResetPassword = hasPermission;
      });
  }

  ngOnDestroy(): void {
    this.locationSubscription?.unsubscribe();
    this.locationTracking.stopTracking();
  }

  loadConsultant(): void {
    this.isLoading = true;
    this.consultantsService
      .getConsultantById(this.consultantId)
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe({
        next: (consultant) => {
          this.consultant = consultant;
          if (consultant.currentLatitude != null && consultant.currentLongitude != null) {
            this.mapMarkers.set([
              { latitude: consultant.currentLatitude, longitude: consultant.currentLongitude, label: consultant.nome ?? 'Consultor' }
            ]);
          }
        },
      });
  }

  loadConsultantToday(): void {
    this.consultantsService.getConsultantToday(this.consultantId).subscribe({
      next: (response) => {
        this.consultantToday = response.status === 200 ? response.body : null;
      },
      error: () => {
        this.consultantToday = null;
      },
    });
  }

  loadPendingItems(): void {
    this.consultantsService.getConsultantPendingItems(this.consultantId).subscribe({
      next: (items) => {
        this.pendingItems = items;
      },
    });
  }

  loadScheduledDays(): void {
    this.isScheduledDaysLoading = true;
    this.consultantsService
      .getConsultantScheduledDays(
        this.consultantId,
        this.scheduledDaysPage + 1,
        this.scheduledDaysPageSize,
        this.scheduledDaysSearch
      )
      .pipe(finalize(() => (this.isScheduledDaysLoading = false)))
      .subscribe({
        next: (result) => {
          this.scheduledDays = result.items;
          this.totalScheduledDays = result.totalCount;
        },
      });
  }

  loadCompletedDays(): void {
    this.isCompletedDaysLoading = true;
    this.consultantsService
      .getConsultantCompletedDays(
        this.consultantId,
        this.completedDaysPage + 1,
        this.completedDaysPageSize,
        this.completedDaysSearch
      )
      .pipe(finalize(() => (this.isCompletedDaysLoading = false)))
      .subscribe({
        next: (result) => {
          this.completedDays = result.items;
          this.totalCompletedDays = result.totalCount;
        },
      });
  }

  onScheduledDaysPageChange(event: { offset: number }): void {
    this.scheduledDaysPage = event.offset;
    this.loadScheduledDays();
  }

  onCompletedDaysPageChange(event: { offset: number }): void {
    this.completedDaysPage = event.offset;
    this.loadCompletedDays();
  }

  navigateBack(): void {
    this.router.navigate(['/consultores']);
  }

  confirmResetPassword(): void {
    if (!this.consultant?.hasEmail) {
      showAlertDialog(
        this.dialog,
        AlertType.Warning,
        'Atenção',
        'Este consultor não possui e-mail cadastrado.'
      );
      return;
    }

    const alertDialogRef = showAlertDialog(
      this.dialog,
      AlertType.Warning,
      'Reenviar Senha',
      'Deseja reenviar a senha para o e-mail do consultor?',
      'Reenviar'
    );

    alertDialogRef.afterClosed().subscribe((result: boolean) => {
      if (result) {
        this.resetPassword();
      }
    });
  }

  resetPassword(): void {
    if (this.isResettingPassword) return;

    this.isResettingPassword = true;
    this.consultantsService
      .resetConsultantPassword(this.consultantId)
      .pipe(finalize(() => (this.isResettingPassword = false)))
      .subscribe({
        next: (result) => {
          if (result.success) {
            showAlertDialog(
              this.dialog,
              AlertType.Success,
              'Sucesso!',
              result.message
            );
          } else {
            showAlertDialog(
              this.dialog,
              AlertType.Error,
              'Erro',
              result.message
            );
          }
        },
      });
  }

  formatPhones(): string {
    const phones: string[] = [];
    if (this.consultant?.telefone1) phones.push(this.consultant.telefone1);
    if (this.consultant?.telefone2) phones.push(this.consultant.telefone2);
    return phones.length > 0 ? phones.join(' / ') : '-';
  }

  get addressLines(): string[] {
    if (!this.consultant) return [];

    const { endereco, enderecoNumero, complemento, bairro, cidade, estado, cep } = this.consultant;
    const street = [endereco?.trim(), enderecoNumero?.trim()].filter(Boolean).join(', ');
    const cityState = [cidade?.trim(), estado?.trim()].filter(Boolean).join(' / ');
    const postalCode = cep?.trim().replace(/^(\d{5})(\d{3})$/, '$1-$2');

    return [street, complemento?.trim(), bairro?.trim(), cityState, postalCode ? `CEP: ${postalCode}` : '']
      .filter((line): line is string => !!line);
  }

  get scheduledDaysStart(): number {
    if (this.totalScheduledDays === 0) return 0;
    return this.scheduledDaysPage * this.scheduledDaysPageSize + 1;
  }

  get scheduledDaysEnd(): number {
    return Math.min((this.scheduledDaysPage + 1) * this.scheduledDaysPageSize, this.totalScheduledDays);
  }

  get completedDaysStart(): number {
    if (this.totalCompletedDays === 0) return 0;
    return this.completedDaysPage * this.completedDaysPageSize + 1;
  }

  get completedDaysEnd(): number {
    return Math.min((this.completedDaysPage + 1) * this.completedDaysPageSize, this.totalCompletedDays);
  }

  get scheduledDaysEmptyMessage(): string {
    if (this.isScheduledDaysLoading) {
      return 'Buscando rotas...';
    }

    return this.scheduledDaysSearch.trim()
      ? 'Nenhuma rota programada encontrada para o termo pesquisado.'
      : 'Nenhuma rota programada para este consultor.';
  }

  get completedDaysEmptyMessage(): string {
    if (this.isCompletedDaysLoading) {
      return 'Buscando rotas...';
    }

    return this.completedDaysSearch.trim()
      ? 'Nenhuma rota realizada encontrada para o termo pesquisado.'
      : 'Nenhuma rota realizada por este consultor.';
  }

  getTodayStatusLabel(): string {
    switch (this.consultantToday?.status) {
      case 'in_progress': return 'Em Andamento';
      case 'completed': return 'Concluído';
      default: return 'Não Iniciado';
    }
  }

  getTodayStatusColor(): BadgeVariant {
    switch (this.consultantToday?.status) {
      case 'in_progress': return 'blue';
      case 'completed': return 'green';
      default: return 'gray';
    }
  }

  private formatTodayDate(): string {
    return `Hoje, ${this.formatDateBr(new Date())}`;
  }

  formatDateBr(value: string | Date | null | undefined): string {
    const date = this.parseDate(value);
    if (!date) {
      return '-';
    }

    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  }

  private parseDate(value: string | Date | null | undefined): Date | null {
    if (!value) {
      return null;
    }

    if (value instanceof Date) {
      return Number.isNaN(value.getTime()) ? null : value;
    }

    if (typeof value !== 'string') {
      return null;
    }

    const trimmedValue = value.trim();
    const dateOnlyMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(trimmedValue);

    if (dateOnlyMatch) {
      const [, year, month, day] = dateOnlyMatch;
      return new Date(Number(year), Number(month) - 1, Number(day));
    }

    const parsedDate = new Date(trimmedValue);
    return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
  }

  onRotasRealizadasFilterChange(values: FilterValues): void {
    this.completedDaysSearch = values.search;
    this.completedDaysPage = 0;
    this.loadCompletedDays();
  }

  onRotasProgramadasFilterChange(values: FilterValues): void {
    this.scheduledDaysSearch = values.search;
    this.scheduledDaysPage = 0;
    this.loadScheduledDays();
  }
}
